<template>
  <v-dialog v-model="dialog" max-width="900px" persistent>
    <v-card class="challenge-creator">
      <!-- Header -->
      <div class="creator-header">
        <div class="header-content">
          <v-icon class="header-icon">mdi-pencil</v-icon>
          <div class="header-text">
            <h2>Edit challenge</h2>
            <p>Modify challenge #{{ challengeId }}</p>
          </div>
        </div>
        <v-btn icon variant="text" @click="closeDialog" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Progress Steps -->
      <div class="progress-steps">
        <div class="step" :class="{ active: step === 1, completed: step > 1 }">
          <div class="step-number">1</div>
          <span>Image</span>
        </div>
        <div class="step-line" :class="{ completed: step > 1 }"></div>
        <div class="step" :class="{ active: step === 2, completed: step > 2 }">
          <div class="step-number">2</div>
          <span>Conditions</span>
        </div>
        <div class="step-line" :class="{ completed: step > 2 }"></div>
        <div class="step" :class="{ active: step === 3, completed: step > 3 }">
          <div class="step-number">3</div>
          <span>Reward</span>
        </div>
      </div>

      <!-- Content -->
      <div class="creator-content">
        <!-- Step 1: Image Upload -->
        <div v-if="step === 1" class="step-content">
          <div class="section-header">
            <v-icon class="section-icon">mdi-image-outline</v-icon>
            <h3>Challenge Image</h3>
          </div>
          
          <div class="image-upload-section">
            <div v-if="!challengeForm.imageUrl" class="upload-area" @click="$refs.fileInput.click()">
              <v-icon size="48" class="upload-icon">mdi-cloud-upload-outline</v-icon>
              <h4>Click to upload image</h4>
              <p>Supported formats: JPG, PNG (Max 5MB)</p>
              <v-btn variant="outlined" :loading="imageUploading" class="upload-btn">
                Choose File
              </v-btn>
            </div>
            
            <div v-else class="image-preview">
              <img :src="challengeForm.imageUrl" alt="Challenge Image" />
              <div class="image-actions">
                <v-btn variant="outlined" @click="$refs.fileInput.click()" :loading="imageUploading">
                  <v-icon left>mdi-image-edit</v-icon>
                  Change Image
                </v-btn>
              </div>
            </div>
            
            <input 
              ref="fileInput" 
              type="file" 
              accept="image/*" 
              @change="handleFileSelect" 
              style="display: none"
            />
          </div>

          <div class="form-group">
            <label class="field-label">Image Credit</label>
            <v-text-field
              v-model="challengeForm.imageBy"
              placeholder="e.g., Photographer Name"
              variant="outlined"
              density="compact"
              class="creator-input"
            ></v-text-field>
          </div>
        </div>

        <!-- Step 2: Conditions -->
        <div v-if="step === 2" class="step-content">
          <div class="section-header">
            <v-icon class="section-icon">mdi-cog-outline</v-icon>
            <h3>Challenge Conditions</h3>
          </div>

          <!-- Race Selection -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-flag-checkered</v-icon>
              <h4>Event</h4>
              <span class="required-badge">Required</span>
            </div>
            <v-select
              v-model="challengeForm.raceID"
              :options="tracks"
              label="name"
              :reduce="track => track.id"
              placeholder="Select a race track"
              :loading="tracksLoading"
              class="creator-select"
            ></v-select>
          </div>

          <!-- Vehicle Restrictions -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-car</v-icon>
              <h4>Vehicle Restrictions</h4>
            </div>
            
            <div class="form-row">
              <div class="form-field">
                <label class="field-label">Car</label>
                <v-select
                  v-model="challengeForm.car"
                  :options="cars"
                  label="name"
                  :reduce="car => car.id"
                  placeholder="Any car allowed"
                  clearable
                  class="creator-select"
                ></v-select>
              </div>
              
              <div class="form-field">
                <label class="field-label">Class</label>
                <v-select
                  v-model="challengeForm.carClass"
                  :options="classOptions"
                  label="title"
                  :reduce="option => option.value"
                  placeholder="Any class allowed"
                  clearable
                  class="creator-select"
                ></v-select>
              </div>
            </div>
          </div>

          <!-- Performance Conditions -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-speedometer</v-icon>
              <h4>Requirements</h4>
              <span class="required-badge">At least 1 required</span>
            </div>

            <div class="performance-grid">
              <!-- Cops Rammed -->
              <div class="performance-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-police-badge</v-icon>
                  <span>Cops Rammed</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorCopsRammed"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <v-text-field
                    v-model="challengeForm.copsRammed"
                    type="number"
                    placeholder="Value"
                    variant="outlined"
                    density="compact"
                    class="value-input"
                  ></v-text-field>
                </div>
              </div>

              <!-- Cops Disabled -->
              <div class="performance-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-police-badge-outline</v-icon>
                  <span>Cops Disabled</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorCopsDisabled"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <v-text-field
                    v-model="challengeForm.copsDisabled"
                    type="number"
                    placeholder="Value"
                    variant="outlined"
                    density="compact"
                    class="value-input"
                  ></v-text-field>
                </div>
              </div>

              <!-- Top Speed -->
              <div class="performance-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-speedometer</v-icon>
                  <span>Top Speed (km/h)</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorTopSpeed"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <v-text-field
                    v-model="challengeForm.topSpeed"
                    type="number"
                    placeholder="200"
                    variant="outlined"
                    density="compact"
                    class="value-input"
                  ></v-text-field>
                </div>
              </div>

              <!-- Cost to State -->
              <div class="performance-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-cash</v-icon>
                  <span>Cost to State</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorCostToState"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <v-text-field
                    v-model="challengeForm.costToState"
                    type="number"
                    placeholder="1500000"
                    variant="outlined"
                    density="compact"
                    class="value-input"
                  ></v-text-field>
                </div>
              </div>

              <!-- Duration -->
              <div class="performance-item duration-item">
                <div class="performance-label">
                  <v-icon class="performance-icon">mdi-timer-outline</v-icon>
                  <span>Race Duration</span>
                </div>
                <div class="performance-inputs">
                  <v-select
                    v-model="challengeForm.operatorDuration"
                    :options="operatorOptions"
                    label="title"
                    :reduce="op => op.value"
                    placeholder="Op."
                    class="operator-select"
                  ></v-select>
                  <div class="duration-inputs">
                    <v-text-field
                      v-model="challengeForm.durationMin"
                      type="number"
                      placeholder="Min"
                      variant="outlined"
                      density="compact"
                      class="duration-input"
                    ></v-text-field>
                    <span class="duration-separator">:</span>
                    <v-text-field
                      v-model="challengeForm.durationSec"
                      type="number"
                      placeholder="Sec"
                      variant="outlined"
                      density="compact"
                      class="duration-input"
                    ></v-text-field>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Game Settings -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-lightning-bolt</v-icon>
              <h4>Restrictions</h4>
            </div>
            
            <div class="form-field">
              <label class="field-label">Powerups</label>
              <v-select
                v-model="challengeForm.powerups"
                :options="powerupOptions"
                label="title"
                :reduce="option => option.value"
                placeholder="No restriction"
                clearable
                class="creator-select"
              ></v-select>
            </div>
          </div>
        </div>

        <!-- Step 3: Reward -->
        <div v-if="step === 3" class="step-content">
          <div class="section-header">
            <v-icon class="section-icon">mdi-gift-outline</v-icon>
            <h3>Challenge Reward</h3>
          </div>

          <!-- Difficulty Selection -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-target</v-icon>
              <h4>Challenge Difficulty</h4>
              <span class="required-badge">Required</span>
            </div>
            
            <div class="form-row">
              <div class="form-field">
                <label class="field-label required-label">Difficulty Level</label>
                <v-select
                  v-model="challengeForm.difficulty"
                  :options="difficultyOptions"
                  label="title"
                  :reduce="option => option.value"
                  placeholder="Select difficulty level"
                  class="creator-select difficulty-select"
                ></v-select>
              </div>
            </div>
            
            <!-- Difficulty Preview -->
            <div v-if="challengeForm.difficulty !== null && challengeForm.difficulty !== undefined" class="difficulty-preview">
              <div class="difficulty-badge" :style="{ backgroundColor: difficultyOptions.find(d => d.value === challengeForm.difficulty)?.color }">
                <v-icon class="difficulty-icon">mdi-target</v-icon>
                {{ difficultyOptions.find(d => d.value === challengeForm.difficulty)?.title }}
              </div>
            </div>
          </div>

          <!-- Reward Configuration -->
          <div class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-gift</v-icon>
              <h4>Reward Configuration</h4>
              <span class="required-badge">Required</span>
            </div>
            
            <div class="form-row">
              <div class="form-field">
                <label class="field-label required-label">Reward Amount</label>
                <v-text-field
                  v-model="challengeForm.reward"
                  type="number"
                  placeholder="1000000"
                  variant="outlined"
                  density="compact"
                  hide-details
                  class="creator-input"
                ></v-text-field>
              </div>
              
              <div class="form-field">
                <label class="field-label required-label">Reward Type</label>
                <v-select
                  v-model="challengeForm.rewardType"
                  :options="rewardTypeOptions"
                  label="title"
                  :reduce="option => option.value"
                  placeholder="Select reward type"
                  class="creator-select reward-type-select"
                ></v-select>
              </div>
            </div>
          </div>

          <!-- Reward Preview -->
          <div v-if="challengeForm.reward && challengeForm.rewardType" class="condition-group">
            <div class="condition-header">
              <v-icon class="condition-icon">mdi-eye</v-icon>
              <h4>Reward Preview</h4>
            </div>
            
            <div class="reward-preview">
              <div class="reward-card">
                <v-icon class="reward-icon">mdi-gift</v-icon>
                <div class="reward-details">
                  <h4>Challenge Reward</h4>
                  <p class="reward-amount">
                    {{ formatNumber(challengeForm.reward) }} 
                    {{ challengeForm.rewardType === '0' ? '$' : 'SB' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Actions -->
      <div class="creator-actions">
        <div class="actions-left">
          <v-btn 
            v-if="step > 1" 
            variant="text" 
            @click="previousStep"
            class="nav-btn"
          >
            <v-icon left>mdi-chevron-left</v-icon>
            Previous
          </v-btn>
        </div>
        
        <div class="actions-right">
          <v-btn 
            variant="text" 
            @click="closeDialog"
            class="cancel-btn"
          >
            Cancel
          </v-btn>
          
          <v-btn 
            v-if="step < 3" 
            color="primary" 
            @click="nextStep"
            :disabled="!canProceedToNext"
            class="nav-btn"
          >
            Next
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
          
          <v-btn 
            v-if="step === 3" 
            color="success" 
            @click="updateChallenge"
            :loading="isUpdating"
            class="create-btn"
          >
            <v-icon left>mdi-check</v-icon>
            Update Challenge
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, computed, watch, nextTick, getCurrentInstance } from 'vue'
import { DriverStore } from '../pinia/Driver.js'

export default {
  name: 'EditChallengeDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    challengeId: {
      type: [String, Number],
      default: null
    }
  },
  emits: ['update:modelValue', 'challenge-updated'],
  setup(props, { emit }) {
    // Store and proxy
    const driverStore = DriverStore()
    const { proxy } = getCurrentInstance()

    // Loading states
    const tracksLoading = ref(false)
    const imageUploading = ref(false)
    const isUpdating = ref(false)

    // Dynamic data
    const tracks = ref([])
    const cars = ref([])

    // Static options from CreateChallengeDialog
    const operatorOptions = [
      { title: 'Greater than (+)', value: '0' },
      { title: 'Less than (-)', value: '1' },
      { title: 'Equal to (=)', value: '2' }
    ]

    const powerupOptions = [
      { title: 'Allowed', value: 'false' },
      { title: 'Disabled', value: 'true' }
    ]

    const classOptions = [
      { title: 'Class E', value: '872416321' },
      { title: 'Class D', value: '415909161' },
      { title: 'Class C', value: '1866825865' },
      { title: 'Class B', value: '-406473455' },
      { title: 'Class A', value: '-405837480' },
      { title: 'Class S', value: '-2142411446' },
      { title: 'Class S1', value: '86241155' },
      { title: 'Class S2', value: '221915816' },
      { title: 'Class X', value: '-543091316' }
    ]

    const rewardTypeOptions = [
      { title: 'Cash ($)', value: '0' },
      { title: 'Speed Boost (SB)', value: '1' }
    ]

    const difficultyOptions = [
      { title: 'Very Easy', value: 0, color: '#4CAF50' },
      { title: 'Easy', value: 1, color: '#8BC34A' },
      { title: 'Medium', value: 2, color: '#FF9800' },
      { title: 'Hard', value: 3, color: '#F44336' },
      { title: 'Very Hard', value: 4, color: '#9C27B0' }
    ]

    const dialog = computed({
      get: () => props.modelValue,
      set: (value) => emit('update:modelValue', value)
    })

    const step = ref(1)

    // Form data
    const challengeForm = ref({
      imageUrl: '',
      imageBy: '',
      raceID: '',
      car: '',
      carClass: '',
      operatorCopsRammed: '0',
      copsRammed: '',
      operatorCopsDisabled: '0',
      copsDisabled: '',
      operatorTopSpeed: '0',
      topSpeed: '',
      operatorCostToState: '0',
      costToState: '',
      operatorDuration: '0',
      durationMin: '',
      durationSec: '',
      powerups: '',
      difficulty: 2,
      reward: '',
      rewardType: '0'
    })

    // Computed properties
    const canProceedToNext = computed(() => {
      switch (step.value) {
        case 1:
          return challengeForm.value.imageUrl && challengeForm.value.imageBy
        case 2:
          return challengeForm.value.raceID
        case 3:
          return challengeForm.value.difficulty !== null && challengeForm.value.reward && challengeForm.value.rewardType
        default:
          return false
      }
    })

    // Helper function to get current week
    const getCurrentWeek = () => {
      const now = new Date()
      const start = new Date(now.getFullYear(), 0, 1)
      const days = Math.floor((now - start) / (24 * 60 * 60 * 1000))
      return Math.ceil((days + start.getDay() + 1) / 7)
    }

    // Load challenge data
    const loadChallengeData = async () => {
      if (!props.challengeId) return

      try {
        const data = await fetch('https://api.nightriderz.world/gateway.php?contentType=application/json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            serviceName: "challengesmanager",
            methodName: "getChallengeById",
            parameters: [props.challengeId]
          })
        }).then(res => res.json())
        
        if (data) {
          // Fill form with existing data
          challengeForm.value.imageUrl = data.image || ''
          challengeForm.value.imageBy = data.image_by || ''
          challengeForm.value.difficulty = parseInt(data.difficulty) || 2
          challengeForm.value.reward = data.reward || ''
          challengeForm.value.rewardType = data.rewardtype?.toString() || '0'
          
          // Parse conditions
          if (data.conditions && Array.isArray(data.conditions)) {
            data.conditions.forEach(condition => {
              switch (condition.type) {
                case 'raceID':
                  challengeForm.value.raceID = condition.value
                  break
                case 'copsRammed':
                  challengeForm.value.copsRammed = condition.value.match(/\d+/)?.[0] || ''
                  break
                case 'copsDisabled':
                  challengeForm.value.copsDisabled = condition.value.match(/\d+/)?.[0] || ''
                  break
                case 'topSpeed':
                  challengeForm.value.topSpeed = condition.value.match(/\d+/)?.[0] || ''
                  break
                case 'costToState':
                  challengeForm.value.costToState = condition.value.match(/\d+/)?.[0] || ''
                  break
                case 'duration':
                  const duration = condition.value.match(/(\d+):(\d+)/)
                  if (duration) {
                    challengeForm.value.durationMin = duration[1]
                    challengeForm.value.durationSec = duration[2]
                  }
                  break
                case 'car':
                  challengeForm.value.car = condition.value
                  break
                case 'carClass':
                  challengeForm.value.carClass = condition.value
                  break
                case 'powerups':
                  challengeForm.value.powerups = condition.value
                  break
              }
            })
          }
        }
      } catch (error) {
        console.error('Error loading challenge data:', error)
      }
    }

    // Load tracks function
    const loadTracks = async () => {
      tracksLoading.value = true
      try {
        const response = await proxy.$api({
          serviceName: "challengesmanager",
          methodName: "GetTrackList",
          parameters: [getCurrentWeek()]
        })
        
        if (Array.isArray(response)) {
          const allTracks = []
          response.forEach(category => {
            if (category.loop && Array.isArray(category.loop)) {
              category.loop.forEach(track => {
                const id = track.id || track.Id || track.ID
                const name = track.name || track.Name || track.DisplayName
                
                if (id && name) {
                  allTracks.push({
                    id: id,
                    name: `${name.trim()} (${category.type})`
                  })
                }
              })
            }
          })
          tracks.value = allTracks
        } else {
          tracks.value = []
        }
      } catch (error) {
        console.error('Error loading tracks:', error)
        tracks.value = []
      } finally {
        tracksLoading.value = false
      }
    }

    // Load cars function
    const loadCars = async () => {
      try {
        const response = await proxy.$api({
          serviceName: "challengesmanager",
          methodName: "GetCarsList",
          parameters: []
        })
        
        if (Array.isArray(response)) {
          const allCars = []
          response.forEach(manufacturer => {
            if (manufacturer.loop && Array.isArray(manufacturer.loop)) {
              manufacturer.loop.forEach(car => {
                const id = car.store_name || car.store_Name || car.Store_Name
                const name = car.model || car.Model
                const brand = manufacturer.marque || manufacturer.Marque
                
                if (id && name && brand) {
                  allCars.push({
                    id: id,
                    name: `${brand} ${name}`
                  })
                }
              })
            }
          })
          cars.value = allCars
        } else {
          cars.value = []
        }
      } catch (error) {
        console.error('Error loading cars:', error)
        cars.value = []
      }
    }

    // Handle file selection
    const handleFileSelect = async (event) => {
      const file = event.target.files[0]
      if (!file) return

      imageUploading.value = true
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('type', 'challenge')
        formData.append('easharpptr-u', localStorage.getItem('easharpptr_u'))
        formData.append('easharpptr-p', localStorage.getItem('easharpptr_p'))

        const response = await fetch('https://api.nightriderz.world/upload.php', {
          method: 'POST',
          body: formData
        })

        if (response.ok) {
          const result = await response.text()
          if (result !== '0' && result.startsWith('https://')) {
            challengeForm.value.imageUrl = result
          } else {
            console.error('Upload failed: Server returned:', result)
          }
        }
      } catch (error) {
        console.error('Upload error:', error)
      } finally {
        imageUploading.value = false
      }
    }

    // Navigation functions
    const nextStep = () => {
      if (step.value < 3) step.value++
    }

    const previousStep = () => {
      if (step.value > 1) step.value--
    }

    // Update challenge function
    const updateChallenge = async () => {
      if (!canProceedToNext.value) return

      isUpdating.value = true
      try {
        const duration = (parseInt(challengeForm.value.durationMin) || 0) * 60 * 1000 + 
                        (parseInt(challengeForm.value.durationSec) || 0) * 1000

        const parameters = [
          props.challengeId,                              // ID (first for update)
          new Date().getFullYear(),                       // year
          getCurrentWeek(),                               // week
          challengeForm.value.type || 'Time Trial',       // type  
          challengeForm.value.raceID || '',               // track
          challengeForm.value.imageBy || '',              // image_by
          challengeForm.value.imageUrl ? challengeForm.value.imageUrl.split('/').pop() : '', // image_url
          challengeForm.value.copsRammed || '',           // copsrammed
          challengeForm.value.operatorCopsRammed || '',   // Operator_copsRammed
          challengeForm.value.copsDisabled || '',         // copsdestroy
          challengeForm.value.operatorCopsDisabled || '', // Operator_copsDisabled
          challengeForm.value.costToState || '',          // costtostate
          challengeForm.value.operatorCostToState || '',  // Operator_costToState
          duration,                                       // duration
          challengeForm.value.operatorDuration || '',     // Operator_duration
          challengeForm.value.powerups || '',             // powerups
          challengeForm.value.carClass || '',             // car_class
          challengeForm.value.car || '',                  // car
          challengeForm.value.reward || '',               // reward
          challengeForm.value.rewardType || '',           // reward_type
          challengeForm.value.topSpeed || '',             // topSpeed
          challengeForm.value.difficulty !== null ? challengeForm.value.difficulty : 2  // difficulty
        ]

        const response = await fetch('https://api.nightriderz.world/gateway.php?contentType=application/json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            serviceName: "challengesmanager",
            methodName: "updateChallenge2",
            parameters: parameters
          })
        })

        console.log('Response status:', response.status)
        const responseText = await response.text()
        console.log('Response text:', responseText)

        // Try to parse as JSON
        let result
        try {
          result = JSON.parse(responseText)
        } catch (parseError) {
          console.error('Failed to parse response as JSON:', parseError)
          console.error('Raw response:', responseText)
          throw new Error('Server returned invalid JSON response')
        }

        if (result && result !== '0') {
          emit('challenge-updated')
          closeDialog()
        } else {
          console.error('Failed to update challenge, result:', result)
        }
      } catch (error) {
        console.error('Error updating challenge:', error)
      } finally {
        isUpdating.value = false
      }
    }

    const closeDialog = () => {
      dialog.value = false
      step.value = 1
    }

    // Format number for display
    const formatNumber = (num) => {
      return parseInt(num).toLocaleString()
    }

    // Load data when dialog opens
    watch(() => props.modelValue, (newVal) => {
      if (newVal && props.challengeId) {
        nextTick(() => {
          loadChallengeData()
          loadTracks()
          loadCars()
        })
      }
    })

    return {
      dialog,
      step,
      isUpdating,
      imageUploading,
      challengeForm,
      difficultyOptions,
      rewardTypeOptions,
      operatorOptions,
      classOptions,
      powerupOptions,
      tracks,
      cars,
      tracksLoading,
      canProceedToNext,
      handleFileSelect,
      nextStep,
      previousStep,
      updateChallenge,
      closeDialog,
      formatNumber
    }
  }
}
</script>

<style scoped>
/* Copied from CreateChallengeDialog */
.challenge-creator {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
  overflow: hidden;
}

.creator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  color: #64ffda;
  font-size: 28px !important;
}

.header-text h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: white;
}

.header-text p {
  margin: 4px 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.close-btn {
  color: rgba(255, 255, 255, 0.7) !important;
}

.close-btn:hover {
  color: white !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 32px;
  gap: 24px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: #64ffda;
  border-color: #64ffda;
  color: #1a1a2e;
}

.step.completed .step-number {
  background: #4caf50;
  border-color: #4caf50;
  color: white;
}

.step span {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.3s ease;
}

.step.active span {
  color: #64ffda;
}

.step.completed span {
  color: #4caf50;
}

.step-line {
  height: 2px;
  width: 60px;
  background: rgba(255, 255, 255, 0.1);
  transition: background 0.3s ease;
}

.step-line.completed {
  background: #4caf50;
}

/* Content */
.creator-content {
  padding: 0 32px 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.step-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.section-icon {
  color: #64ffda;
  font-size: 24px !important;
}

.section-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: white;
}

/* Image Upload */
.image-upload-section {
  margin-bottom: 24px;
}

.upload-area {
  border: 2px dashed rgba(100, 255, 218, 0.3);
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(100, 255, 218, 0.02);
}

.upload-area:hover {
  border-color: rgba(100, 255, 218, 0.5);
  background: rgba(100, 255, 218, 0.05);
}

.upload-icon {
  color: #64ffda;
  margin-bottom: 16px;
}

.upload-area h4 {
  margin: 0 0 8px;
  font-size: 18px;
  color: white;
}

.upload-area p {
  margin: 0 0 24px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
}

.upload-btn {
  color: #64ffda !important;
  border-color: #64ffda !important;
}

.image-preview {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
}

.image-preview img {
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;
}

.image-actions {
  position: absolute;
  top: 12px;
  right: 12px;
}

/* Form Groups */
.form-group {
  margin-bottom: 24px;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: white;
}

.creator-input {
  background: rgba(255, 255, 255, 0.05) !important;
  border-radius: 8px !important;
}

/* Condition Groups */
.condition-group {
  margin-bottom: 32px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.condition-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.condition-icon {
  color: #64ffda;
  font-size: 20px !important;
}

.condition-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.required-badge {
  background: #ff5722;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
}

/* Form Rows */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
}

/* Performance Grid */
.performance-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
}

.performance-item {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 16px;
}

.performance-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.performance-icon {
  color: #64ffda;
  font-size: 16px !important;
}

.performance-label span {
  font-size: 14px;
  font-weight: 500;
  color: white;
}

.performance-inputs {
  display: flex;
  gap: 8px;
  align-items: center;
}

.operator-select {
  flex: 0 0 100px;
}

.value-input {
  flex: 1;
}

.duration-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.duration-input {
  flex: 1;
}

.duration-separator {
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
}

.duration-item .performance-inputs {
  align-items: flex-start;
}

/* Selects */
:deep(.v-select),
:deep(.v-text-field) {
  background: rgba(255, 255, 255, 0.05) !important;
}

:deep(.v-field__input) {
  color: white !important;
}

:deep(.v-field__outline) {
  --v-field-border-color: rgba(255, 255, 255, 0.2);
}

:deep(.v-field--focused .v-field__outline) {
  --v-field-border-color: #64ffda;
}

/* Difficulty */
.difficulty-preview {
  margin-top: 16px;
}

.difficulty-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  color: white;
}

/* Reward Preview */
.reward-preview {
  margin-top: 16px;
}

.reward-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: rgba(100, 255, 218, 0.1);
  border: 1px solid rgba(100, 255, 218, 0.2);
  border-radius: 12px;
}

.reward-icon {
  color: #64ffda;
  font-size: 32px !important;
}

.reward-details h4 {
  margin: 0 0 4px;
  font-size: 16px;
  color: white;
}

.reward-amount {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #64ffda;
}

/* Actions */
.creator-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.02);
}

.actions-left,
.actions-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nav-btn {
  background: rgba(100, 255, 218, 0.1) !important;
  color: #64ffda !important;
  border: 1px solid rgba(100, 255, 218, 0.3) !important;
}

.nav-btn:hover {
  background: rgba(100, 255, 218, 0.2) !important;
}

.cancel-btn {
  color: rgba(255, 255, 255, 0.7) !important;
}

.cancel-btn:hover {
  color: white !important;
  background: rgba(255, 255, 255, 0.1) !important;
}

.create-btn {
  background: #4caf50 !important;
  color: white !important;
}

.create-btn:hover {
  background: #45a049 !important;
}
</style>